import { useRef, useState } from 'react';
import { Link, createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MIMES_AUDIO, ROTAS, type EstadoGravacao } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar, ErroApi } from '../api';
import { usarSessao } from '../estado/sessao';
import {BotaoLeve, Cartao} from '../componentes/layout';
import { Tela } from '../workspace/Tela';
import { mensagemDoErro } from '../erros';

export interface GravacaoNaLista {
  id: string;
  titulo: string;
  estado: EstadoGravacao;
  duracaoMs: number | null;
  criadoEm: string;
  donoNome: string;
  segmentos: number;
  trechos: number;
  compartilhada: boolean;
  erro: string | null;
}

const ROTULO_ESTADO: Record<EstadoGravacao, string> = {
  AGUARDANDO_ENVIO: 'aguardando envio',
  GRAVANDO: 'gravando',
  PROCESSANDO: 'preparando o áudio',
  TRANSCREVENDO: 'transcrevendo',
  CONCLUIDA: 'pronta',
  ERRO: 'erro',
};

export function duracaoLegivel(ms: number | null): string {
  if (!ms) return '—';
  const total = Math.round(ms / 1000);
  const minutos = Math.floor(total / 60);
  const segundos = total % 60;
  return `${minutos}:${String(segundos).padStart(2, '0')}`;
}

interface Achado {
  gravacaoId: string;
  titulo: string;
  seq: number;
  inicioMs: number;
  trecho: string;
}

/**
 * Busca dentro do que foi falado.
 *
 * O servidor devolve o trecho já com o termo marcado entre « »; aqui isso vira
 * destaque, e o clique leva ao ponto do áudio em que a frase foi dita.
 */
function Busca(): React.JSX.Element {
  const [termo, setTermo] = useState('');
  const [consultado, setConsultado] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: ['busca-transcricao', consultado],
    queryFn: () =>
      chamar<{ itens: Achado[] }>(`${ROTAS.gravacoes}/busca?q=${encodeURIComponent(consultado)}`),
    enabled: consultado.length >= 2,
  });

  return (
    <Cartao titulo="Buscar no que foi dito">
      <form
        className="flex gap-2"
        onSubmit={(evento) => {
          evento.preventDefault();
          setConsultado(termo.trim());
        }}
      >
        <input
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Uma palavra ou frase..."
          className="flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-600"
        />
        <BotaoLeve type="submit" disabled={termo.trim().length < 2}>
          Buscar
        </BotaoLeve>
      </form>

      {isFetching ? <p className="mt-3 text-sm text-zinc-500">Procurando…</p> : null}
      {data && consultado ? (
        data.itens.length === 0 ? (
          <p className="mt-3 text-sm text-zinc-500">Nada encontrado para “{consultado}”.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.itens.map((achado) => (
              <li key={achado.gravacaoId + '-' + achado.seq}>
                <Link
                  to="/audio/$id"
                  params={{ id: achado.gravacaoId }}
                  className="block rounded-md border border-zinc-800 px-3 py-2 hover:bg-zinc-800/50"
                >
                  <span className="text-xs text-zinc-500">
                    {achado.titulo} · {duracaoLegivel(achado.inicioMs)}
                  </span>
                  <p className="mt-0.5 text-sm text-zinc-300">
                    {achado.trecho.split(/[«»]/).map((parte, i) =>
                      i % 2 === 1 ? (
                        <mark key={i} className="bg-emerald-500/20 text-emerald-200">
                          {parte}
                        </mark>
                      ) : (
                        <span key={i}>{parte}</span>
                      ),
                    )}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Cartao>
  );
}

function Audio(): React.JSX.Element {
  const cliente = useQueryClient();
  const papel = usarSessao((e) => e.usuario?.papel);
  const podeEnviar = papel === 'ADMIN' || papel === 'OPERADOR';
  const entrada = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState('');
  const [progresso, setProgresso] = useState('');

  const { data: gravacoes = [] } = useQuery({
    queryKey: ['gravacoes'],
    queryFn: () => chamar<GravacaoNaLista[]>(ROTAS.gravacoes),
    // Enquanto houver gravação em andamento, a lista se atualiza sozinha.
    refetchInterval: (consulta) =>
      (consulta.state.data ?? []).some(
        (g) => g.estado === 'PROCESSANDO' || g.estado === 'TRANSCREVENDO',
      )
        ? 3_000
        : false,
  });

  const enviar = useMutation({
    mutationFn: async (arquivo: File) => {
      setErro('');
      setProgresso('pedindo o link de envio…');
      const criada = await chamar<{ gravacaoId: string; urlEnvio: string }>(ROTAS.gravacoes, {
        method: 'POST',
        corpo: {
          titulo: arquivo.name.replace(/\.[^.]+$/, ''),
          mime: arquivo.type,
          tamanho: arquivo.size,
        },
      });

      // O arquivo vai direto para o armazenamento: nunca passa pela API.
      setProgresso('enviando o áudio…');
      const envio = await fetch(criada.urlEnvio, {
        method: 'PUT',
        headers: { 'content-type': arquivo.type },
        body: arquivo,
      });
      if (!envio.ok) throw new ErroApi(envio.status, 'envio_falhou', 'falha no envio');

      // O disco devolve o etag no corpo; o R2, no cabeçalho.
      const doCorpo = (await envio.json().catch(() => ({}))) as { etag?: string };
      const etag = doCorpo.etag ?? (envio.headers.get('etag') ?? '').replaceAll('"', '') ?? '';

      setProgresso('confirmando…');
      return chamar(`${ROTAS.gravacoes}/${criada.gravacaoId}/confirmar`, {
        method: 'POST',
        corpo: { etag: etag || 'sem-etag', tamanho: arquivo.size },
      });
    },
    onSuccess: () => {
      setProgresso('');
      if (entrada.current) entrada.current.value = '';
      void cliente.invalidateQueries({ queryKey: ['gravacoes'] });
    },
    onError: (e) => {
      setProgresso('');
      setErro(
        mensagemDoErro(e, 'Não foi possível enviar.', {
          validacao: 'Formato de áudio não aceito.',
          sem_permissao: 'Seu perfil não pode enviar áudio.',
        }),
      );
    },
  });

  return (
    <Tela>
      {erro ? (
        <p className="rounded-md border border-red-900 bg-red-950/40 px-4 py-2 text-sm text-red-200">
          {erro}
        </p>
      ) : null}

      {podeEnviar ? (
        <Cartao
          titulo="Gravar agora"
          acao={
            <Link to="/audio/gravar">
              <BotaoLeve>Abrir o microfone</BotaoLeve>
            </Link>
          }
        >
          <p className="text-sm text-zinc-400">
            Grave direto pelo navegador, no computador ou no celular. O texto aparece enquanto você
            fala, e o áudio fica guardado no aparelho até conseguir subir.
          </p>
        </Cartao>
      ) : null}

      {podeEnviar ? (
        <Cartao titulo="Enviar áudio">
          <input
            ref={entrada}
            type="file"
            accept={MIMES_AUDIO.join(',')}
            disabled={enviar.isPending}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo) enviar.mutate(arquivo);
            }}
            className="block w-full text-sm text-zinc-400 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-zinc-900"
          />
          <p className="mt-2 text-xs text-zinc-500">
            {progresso || 'O áudio vai direto para o armazenamento; a transcrição começa sozinha.'}
          </p>
        </Cartao>
      ) : null}

      <Busca />

      <Cartao titulo="Gravações">
        {gravacoes.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhuma gravação ainda.</p>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {gravacoes.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <Link
                    to="/audio/$id"
                    params={{ id: g.id }}
                    className="font-medium text-zinc-100 hover:underline"
                  >
                    {g.titulo}
                  </Link>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {ROTULO_ESTADO[g.estado]} · {duracaoLegivel(g.duracaoMs)} · {g.segmentos} trecho(s)
                    de texto · {g.donoNome}
                    {g.compartilhada ? ' · compartilhada' : ''}
                  </p>
                  {g.erro ? <p className="mt-0.5 text-xs text-red-300">{g.erro}</p> : null}
                </div>
                <Link to="/audio/$id" params={{ id: g.id }}>
                  <BotaoLeve>Abrir</BotaoLeve>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </Tela>
  );
}

export const rotaAudio = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/audio',
  component: Audio,
});
